import * as React from "react";
import { Link } from "react-router-dom";
import {
  PageHeader,
  PageHeaderTop,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  DataTable,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DataTableScroll,
  DataTableEmpty,
  DataTableSkeleton,
  DataTableFooter,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  StatusPill,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  StatCard,
  cn,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import {
  Archive,
  CalendarClock,
  CalendarOff,
  CalendarRange,
  CheckCircle2,
  Clock,
  Layers3,
  Loader2,
  MoreHorizontal,
  Play,
  RefreshCw,
  Sparkles,
  Users2,
  X,
} from "lucide-react";
import type {
  TimetableJobStatusValue,
  TimetableJobView,
  TimetableStatusValue,
  ValidityKind,
  TimetableView,
} from "@eduviaos/shared";
import {
  useArchiveTimetable,
  useCancelTimetableJob,
  usePublishTimetable,
  useTimetableJobs,
  useTimetables,
} from "@/hooks/useTimetable";
import { QuickGenerateSheet } from "@/components/timetable/QuickGenerateSheet";

/**
 * Phase G3 FE-1a — IA timetable hub.
 *
 * Two complementary panels backed by the BE-2c admin/scheduling
 * routes:
 *
 *   1. **Solver jobs** — every CP-SAT invocation against the IA's
 *      frozen :class:`SolverInputs`. Surfaces queue/run/terminal
 *      state, elapsed time, objective score, and a row-level cancel
 *      affordance while the job is in flight. Failed jobs link to
 *      the structured conflict report (via the detail page in
 *      G3 FE-1b).
 *
 *   2. **Timetables** — solver-produced + IA-edited weekly schedules.
 *      Lifecycle is draft → published → archived. The list shows
 *      the active publish, recent drafts, and archived runs; per-row
 *      actions hand off to the detail/grid editor (G3 FE-1b).
 *
 * Why one page with two panels (instead of two routes): the IA's
 * mental model is a single "timetable workspace" — they queue a
 * solver, watch it run, then open the produced draft in the editor.
 * The tabs let us preserve the workspace metaphor while keeping
 * each table focused on one document type.
 *
 * Phase G3 FE-2 wires the **New solver job** CTA to the
 * BE-3 `:from-catalogue` endpoint via :func:`QuickGenerateSheet`,
 * so admins can queue a run without hand-rolling the full
 * SolverInputs dossier — the BE synthesises every demand /
 * eligibility row from the live catalogue.
 *
 * Teacher view ships in G3 FE-1e.
 */
export function TimetablePage() {
  const [tab, setTab] = React.useState<"jobs" | "timetables">("jobs");
  const [generateOpen, setGenerateOpen] = React.useState(false);

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Timetable</PageHeaderHeading>
            <PageHeaderDescription>
              Generate, edit, and publish weekly timetables. Start a generation, watch it run, then
              open the produced draft to edit slots and publish.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            {/* G3.5 — Holidays authoring lives next to the timetable
                workspace because the solver consumes the same rows
                as off-days. Surfacing the link here lets admins jump
                between the two without hunting through the sidebar. */}
            <Button asChild variant="outline" size="sm" data-testid="timetable-holidays-link">
              <Link to="/holidays">
                <CalendarOff aria-hidden="true" className="size-4" />
                Holidays
              </Link>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setGenerateOpen(true)}
              data-testid="timetable-new-job"
            >
              <Sparkles aria-hidden="true" className="size-4" />
              New schedule
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <KpiStrip />

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="jobs">
            <Layers3 className="size-4" aria-hidden="true" />
            Generation jobs
          </TabsTrigger>
          <TabsTrigger value="timetables">
            <CalendarRange className="size-4" aria-hidden="true" />
            Timetables
          </TabsTrigger>
        </TabsList>
        <TabsContent value="jobs">
          <SolverJobsPanel />
        </TabsContent>
        <TabsContent value="timetables">
          <TimetablesPanel />
        </TabsContent>
      </Tabs>

      <QuickGenerateSheet open={generateOpen} onOpenChange={setGenerateOpen} />
    </div>
  );
}

// ── KPI strip ────────────────────────────────────────────────────────

function KpiStrip() {
  // Pull a fresh snapshot of jobs + timetables for the headline KPIs.
  // We lean on the same lists the panels render, so every fetch is
  // shared across the page (TanStack Query dedupes on the queryKey).
  const jobs = useTimetableJobs();
  const timetables = useTimetables();

  const runningCount = (jobs.data?.jobs ?? []).filter(
    (j) => j.status === "running" || j.status === "queued",
  ).length;

  const publishedTimetable = (timetables.data?.timetables ?? []).find(
    (t) => t.status === "published",
  );

  const draftCount = (timetables.data?.timetables ?? []).filter((t) => t.status === "draft").length;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label="Active jobs"
        value={runningCount}
        tone={runningCount > 0 ? "violet" : "neutral"}
        icon={<Loader2 className="size-4" aria-hidden="true" />}
        caption={runningCount > 0 ? "In progress" : "No jobs in flight"}
      />
      <StatCard
        label="Drafts"
        value={draftCount}
        tone={draftCount > 0 ? "amber" : "neutral"}
        icon={<Layers3 className="size-4" aria-hidden="true" />}
        caption="Awaiting review or publish"
      />
      <StatCard
        label="Active timetable"
        value={publishedTimetable ? `${publishedTimetable.section_count} sections` : "—"}
        tone={publishedTimetable ? "green" : "neutral"}
        icon={<CalendarClock className="size-4" aria-hidden="true" />}
        caption={publishedTimetable ? `“${publishedTimetable.name}”` : "No published timetable"}
      />
      <StatCard
        label="Teachers scheduled"
        value={publishedTimetable ? publishedTimetable.teacher_count : 0}
        tone="blue"
        icon={<Users2 className="size-4" aria-hidden="true" />}
        caption="On the active timetable"
      />
    </div>
  );
}

// ── Solver jobs panel ────────────────────────────────────────────────

const JOB_STATUS_PILL: Record<TimetableJobStatusValue, StatusPillStatus> = {
  queued: "pending",
  running: "processing",
  succeeded: "success",
  failed: "failed",
  cancelled: "archived",
};

const JOB_STATUS_LABEL: Record<TimetableJobStatusValue, string> = {
  queued: "Queued",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  cancelled: "Cancelled",
};

function SolverJobsPanel() {
  const jobs = useTimetableJobs();
  const cancelJob = useCancelTimetableJob();

  const items = jobs.data?.jobs ?? [];

  return (
    <DataTable>
      <DataTableToolbar>
        <div className="text-muted-foreground text-xs">
          {jobs.isLoading
            ? "Loading…"
            : `${items.length} generation job${items.length === 1 ? "" : "s"}`}
        </div>
        <DataTableToolbarSpacer />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void jobs.refetch()}
          disabled={jobs.isFetching}
        >
          <RefreshCw
            aria-hidden="true"
            className={cn("size-4", jobs.isFetching && "animate-spin")}
          />
          Refresh
        </Button>
      </DataTableToolbar>
      <DataTableScroll>
        <Table>
          <caption className="sr-only">Generation jobs</caption>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Elapsed</TableHead>
              <TableHead className="text-right">Objective</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          {jobs.isLoading ? (
            <DataTableSkeleton columns={6} rows={4} />
          ) : (
            <TableBody>
              {items.map((job) => (
                <SolverJobRow
                  key={job.id}
                  job={job}
                  cancelling={cancelJob.isPending && cancelJob.variables?.jobId === job.id}
                  onCancel={() =>
                    cancelJob.mutate(
                      { jobId: job.id },
                      {
                        onSuccess: () => {
                          toast.success(`Cancelled "${job.name}".`);
                        },
                        onError: (caught) => {
                          const message = caught instanceof Error ? caught.message : String(caught);
                          toast.error(message || "Could not cancel — please try again.");
                        },
                      },
                    )
                  }
                />
              ))}
            </TableBody>
          )}
        </Table>
      </DataTableScroll>
      {!jobs.isLoading && items.length === 0 ? (
        <DataTableEmpty
          icon={<Sparkles className="size-6" aria-hidden="true" />}
          title="No schedules generated yet"
          description="Click 'New schedule' to queue your first timetable run. We'll build a draft from your active subjects, sections, and teacher availability — usually in under a minute."
        />
      ) : null}
      <DataTableFooter>
        <div />
      </DataTableFooter>
    </DataTable>
  );
}

interface SolverJobRowProps {
  job: TimetableJobView;
  cancelling: boolean;
  onCancel: () => void;
}

function SolverJobRow({ job, cancelling, onCancel }: SolverJobRowProps) {
  const isInFlight = job.status === "queued" || job.status === "running";
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{job.name}</div>
        {job.description ? (
          <div className="text-muted-foreground line-clamp-1 text-xs">{job.description}</div>
        ) : null}
      </TableCell>
      <TableCell>
        <StatusPill status={JOB_STATUS_PILL[job.status]} label={JOB_STATUS_LABEL[job.status]} />
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatElapsed(job.elapsed_seconds)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatObjective(job.objective_score)}
      </TableCell>
      <TableCell>
        <time
          dateTime={job.created_at}
          title={new Date(job.created_at).toISOString()}
          className="text-muted-foreground text-xs"
        >
          {formatRelativeOrDate(job.created_at)}
        </time>
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Open menu for ${job.name}`}>
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {job.draft_timetable_id ? (
              <DropdownMenuItem asChild>
                <Link to={`/timetable/${job.draft_timetable_id}`}>
                  <Play className="size-4" aria-hidden="true" />
                  Open draft
                </Link>
              </DropdownMenuItem>
            ) : null}
            {isInFlight ? (
              <>
                {job.draft_timetable_id ? <DropdownMenuSeparator /> : null}
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    onCancel();
                  }}
                  disabled={cancelling}
                  className="text-destructive focus:text-destructive"
                >
                  <X className="size-4" aria-hidden="true" />
                  {cancelling ? "Cancelling…" : "Cancel job"}
                </DropdownMenuItem>
              </>
            ) : null}
            {!isInFlight && !job.draft_timetable_id ? (
              <DropdownMenuItem disabled>
                <Clock className="size-4" aria-hidden="true" />
                No actions available
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

// ── Timetables panel ─────────────────────────────────────────────────

const TIMETABLE_STATUS_PILL: Record<TimetableStatusValue, StatusPillStatus> = {
  draft: "pending",
  published: "active",
  archived: "archived",
};

const TIMETABLE_STATUS_LABEL: Record<TimetableStatusValue, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

const VALIDITY_KIND_LABEL: Record<ValidityKind, string> = {
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  term: "Term",
  semester: "Semester",
  yearly: "Yearly",
  custom: "Custom",
};

function TimetablesPanel() {
  const timetables = useTimetables();
  const publish = usePublishTimetable();
  const archive = useArchiveTimetable();

  const items = timetables.data?.timetables ?? [];

  return (
    <DataTable>
      <DataTableToolbar>
        <div className="text-muted-foreground text-xs">
          {timetables.isLoading
            ? "Loading…"
            : `${items.length} timetable${items.length === 1 ? "" : "s"}`}
        </div>
        <DataTableToolbarSpacer />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void timetables.refetch()}
          disabled={timetables.isFetching}
        >
          <RefreshCw
            aria-hidden="true"
            className={cn("size-4", timetables.isFetching && "animate-spin")}
          />
          Refresh
        </Button>
      </DataTableToolbar>
      <DataTableScroll>
        <Table>
          <caption className="sr-only">Saved timetables</caption>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Sections</TableHead>
              <TableHead className="text-right">Teachers</TableHead>
              <TableHead className="text-right">Slots</TableHead>
              <TableHead>Effective from</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          {timetables.isLoading ? (
            <DataTableSkeleton columns={7} rows={4} />
          ) : (
            <TableBody>
              {items.map((timetable) => (
                <TimetableRow
                  key={timetable.id}
                  timetable={timetable}
                  publishing={publish.isPending && publish.variables?.timetableId === timetable.id}
                  archiving={archive.isPending && archive.variables?.timetableId === timetable.id}
                  onPublish={() =>
                    publish.mutate(
                      { timetableId: timetable.id },
                      {
                        onSuccess: () => {
                          toast.success(`Published "${timetable.name}".`);
                        },
                        onError: (caught) => {
                          const message = caught instanceof Error ? caught.message : String(caught);
                          toast.error(message || "Could not publish — please try again.");
                        },
                      },
                    )
                  }
                  onArchive={() =>
                    archive.mutate(
                      { timetableId: timetable.id },
                      {
                        onSuccess: () => {
                          toast.success(`Archived "${timetable.name}".`);
                        },
                        onError: (caught) => {
                          const message = caught instanceof Error ? caught.message : String(caught);
                          toast.error(message || "Could not archive — please try again.");
                        },
                      },
                    )
                  }
                />
              ))}
            </TableBody>
          )}
        </Table>
      </DataTableScroll>
      {!timetables.isLoading && items.length === 0 ? (
        <DataTableEmpty
          icon={<CalendarRange className="size-6" aria-hidden="true" />}
          title="No timetables yet"
          description="Once a schedule generation succeeds, the produced draft will appear here. Open the draft to edit slots, then publish to make it active."
        />
      ) : null}
      <DataTableFooter>
        <div />
      </DataTableFooter>
    </DataTable>
  );
}

interface TimetableRowProps {
  timetable: TimetableView;
  publishing: boolean;
  archiving: boolean;
  onPublish: () => void;
  onArchive: () => void;
}

function TimetableRow({
  timetable,
  publishing,
  archiving,
  onPublish,
  onArchive,
}: TimetableRowProps) {
  const canPublish = timetable.status === "draft";
  const canArchive = timetable.status === "draft" || timetable.status === "published";

  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col">
          <Link
            to={`/timetable/${timetable.id}`}
            className="font-medium underline-offset-2 hover:underline"
          >
            {timetable.name}
          </Link>
          {timetable.description ? (
            <div className="text-muted-foreground line-clamp-1 text-xs">
              {timetable.description}
            </div>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <StatusPill
          status={TIMETABLE_STATUS_PILL[timetable.status]}
          label={TIMETABLE_STATUS_LABEL[timetable.status]}
        />
      </TableCell>
      <TableCell className="text-right tabular-nums">{timetable.section_count}</TableCell>
      <TableCell className="text-right tabular-nums">{timetable.teacher_count}</TableCell>
      <TableCell className="text-right tabular-nums">{timetable.total_slots}</TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span className="text-muted-foreground text-xs">
            <time dateTime={timetable.effective_from}>{formatDate(timetable.effective_from)}</time>
            {timetable.effective_to ? <> – {formatDate(timetable.effective_to)}</> : null}
          </span>
          <span className="text-muted-foreground/80 text-[10px] uppercase tracking-wide">
            {VALIDITY_KIND_LABEL[timetable.validity_kind] ?? timetable.validity_kind}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Open menu for ${timetable.name}`}>
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/timetable/${timetable.id}`}>
                <Play className="size-4" aria-hidden="true" />
                Open editor
              </Link>
            </DropdownMenuItem>
            {canPublish ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    onPublish();
                  }}
                  disabled={publishing}
                >
                  <CheckCircle2 className="size-4" aria-hidden="true" />
                  {publishing ? "Publishing…" : "Publish"}
                </DropdownMenuItem>
              </>
            ) : null}
            {canArchive ? (
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault();
                  onArchive();
                }}
                disabled={archiving}
                className="text-destructive focus:text-destructive"
              >
                <Archive className="size-4" aria-hidden="true" />
                {archiving ? "Archiving…" : "Archive"}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

// ── Formatters ───────────────────────────────────────────────────────

function formatElapsed(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds - m * 60);
  return `${m}m ${s}s`;
}

function formatObjective(score: number | null): string {
  if (score === null || score === undefined) return "—";
  return score.toFixed(2);
}

function formatRelativeOrDate(iso: string): string {
  const at = new Date(iso);
  const now = Date.now();
  const diffMs = now - at.getTime();
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return "just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  return at.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDate(isoOrDate: string): string {
  // The BE serialises date-only fields as ``YYYY-MM-DD`` and datetime
  // fields as ISO 8601. Both parse fine via ``new Date()``; the
  // returned label uses the locale's short date pattern.
  const at = new Date(isoOrDate);
  if (Number.isNaN(at.getTime())) return isoOrDate;
  return at.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
